// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { formatHumanName } from '@medplum/core';
import type { CarePlan, MedicationRequest, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  carePlanToTreatment,
  getTherapyOrderName,
  getTreatmentLegacyKey,
  isTreatmentOrder,
  legacyTreatmentKeys,
  TREATMENT_CARE_PLAN_CATEGORY_CODE,
  TREATMENT_CARE_PLAN_CATEGORY_SYSTEM,
  therapyOrderStartDate,
} from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useState } from 'react';
import { getPatientTreatments, type PatientTreatment } from '../utils/patient-treatments';

export interface StoredTreatment extends PatientTreatment {
  /** The treatment CarePlan; absent for legacy-extension rows and DME orders that have no CarePlan yet. */
  carePlan?: CarePlan;
  /** Set on rows still read from the legacy Patient extension; saving one creates its CarePlan. */
  legacyKey?: string;
}

export interface DmeTreatment extends StoredTreatment {
  dme: true;
  orderName: string;
}

export type Treatment = StoredTreatment | DmeTreatment;

/** The persisted fields of a treatment row, without UI and lookup state. */
export function treatmentFields(treatment: Treatment): Omit<PatientTreatment, 'id'> {
  return {
    modality: treatment.modality,
    start: treatment.start,
    end: treatment.end,
    other: treatment.other,
    addedBy: treatment.addedBy,
    endedBy: treatment.endedBy,
    source: 'dme' in treatment ? 'dme' : (treatment.source ?? 'manual'),
    orderId: treatment.orderId,
    orderName: treatment.orderName,
  };
}

async function resolveAddedByNames<T extends PatientTreatment>(
  medplum: ReturnType<typeof useMedplum>,
  treatments: T[]
): Promise<T[]> {
  const references = [...new Set(treatments.map((treatment) => treatment.addedBy).filter(Boolean))] as string[];
  const names = new Map<string, string>();
  await Promise.all(
    references.map(async (reference) => {
      if (!reference.startsWith('Practitioner/')) {
        return;
      }
      const practitioner = await medplum.readReference({ reference }).catch(() => undefined);
      if (practitioner?.resourceType === 'Practitioner') {
        const name = practitioner.name?.[0] ? formatHumanName(practitioner.name[0]) : undefined;
        if (name) {
          names.set(reference, name);
        }
      }
    })
  );
  return treatments.map((treatment) => ({
    ...treatment,
    addedByName: treatment.addedBy ? (names.get(treatment.addedBy) ?? treatment.addedBy) : undefined,
  }));
}

export function usePatientTreatments(patient: Patient): {
  treatments: Treatment[];
  loading: boolean;
  refresh: () => Promise<void>;
} {
  const medplum = useMedplum();
  const [treatments, setTreatments] = useState<Treatment[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const patientId = patient.id ?? '';
      const [carePlans, orders, updatedPatient] = await Promise.all([
        medplum.searchResources(
          'CarePlan',
          {
            subject: `Patient/${patientId}`,
            category: `${TREATMENT_CARE_PLAN_CATEGORY_SYSTEM}|${TREATMENT_CARE_PLAN_CATEGORY_CODE}`,
            'status:not': 'entered-in-error',
            _count: '100',
          },
          { cache: 'no-cache' }
        ),
        (async () => {
          const allOrders: MedicationRequest[] = [];
          for await (const page of medplum.searchResourcePages('MedicationRequest', {
            subject: `Patient/${patientId}`,
            _sort: '-authoredon',
          })) {
            allOrders.push(...(page as MedicationRequest[]));
          }
          return allOrders;
        })(),
        medplum.readResource('Patient', patientId),
      ]);
      const therapyOrders = orders.filter(isTreatmentOrder);

      const stored: Treatment[] = [];
      for (const carePlan of carePlans) {
        const treatment = carePlanToTreatment(carePlan);
        if (!treatment) {
          continue;
        }
        if (treatment.source === 'dme' || treatment.orderId) {
          const order = therapyOrders.find((item) => item.id === treatment.orderId);
          stored.push({
            ...treatment,
            carePlan,
            dme: true,
            orderName: treatment.orderName ?? (order ? getTherapyOrderName(order) : 'DME'),
          });
        } else {
          stored.push({ ...treatment, carePlan });
        }
      }

      // Until the backfill has run, treatments may exist only on the legacy Patient extension or as an order.
      const coveredOrderIds = new Set(stored.map((treatment) => treatment.orderId).filter(Boolean));
      const coveredLegacyKeys = new Set(carePlans.map(getTreatmentLegacyKey).filter(Boolean));
      const legacy = getPatientTreatments(updatedPatient);
      const manual = legacy.filter((treatment) => treatment.source !== 'dme');
      const manualKeys = legacyTreatmentKeys(patientId, manual);
      const legacyManual: Treatment[] = manual
        .map((treatment, index) => ({ ...treatment, legacyKey: manualKeys[index] }))
        .filter((treatment) => !coveredLegacyKeys.has(treatment.legacyKey));
      const uncoveredDme = therapyOrders
        .filter((order) => !order.id || !coveredOrderIds.has(order.id))
        .map((order): DmeTreatment => {
          const override = legacy.find((treatment) => treatment.source === 'dme' && treatment.orderId === order.id);
          return {
            id: `dme-${order.id ?? order.authoredOn}`,
            modality: 'DME',
            // Older orders lack authoredOn; the backfill uses their exact creation date, this approximates it.
            start: override?.start ?? therapyOrderStartDate(order) ?? '',
            end: override?.end,
            endedBy: override?.endedBy,
            source: 'dme',
            dme: true,
            orderId: order.id,
            orderName: getTherapyOrderName(order),
          };
        });

      const all = await resolveAddedByNames(medplum, [...stored, ...legacyManual, ...uncoveredDme]);
      setTreatments(all.filter((treatment) => treatment.start));
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [medplum, patient.id]);

  useEffect(() => {
    refresh().catch(() => undefined);
  }, [refresh]);

  return { treatments, loading, refresh };
}
